/**
 * @packageDocumentation
 * @module f1/fn
 */

/**
 * @param v the value to the g which is passed to f which is returned
 */
export type ConvergeValue<V, W1, W2, Y> = (value: V) => Y;

export interface Converge {
  /**
   * Converge the output of two functions to a new function of two parameters
   *
   * @public
   * @function f1.converge
   * @param f the third function which returns the value of g and h as `f(g, h)`
   * @param g the value to the first paramter of f
   * @param h the value to the second paramter of f
   * @param v the value to the g and h functions
   * @example
   *
   * ```ts
   * f1.converge(f, g, h)(1, 2) => f(g(1, 2), h(1, 2));
   * ```
   */
  <F extends (w1: W1, w2: W2) => Y, G extends (v: V) => W1, H extends (v: V) => W2, V, W1, W2, Y>(
    f: F,
    g: G,
    h: H,
    value: V
  ): Y;
  /**
   * @param f the third function which returns the value of g and h as `f(g, h)`
   * @param g the value to the first paramter of f
   * @param h the value to the second paramter of f
   */
  <F extends (w1: W1, w2: W2) => Y, G extends (v: V) => W1, H extends (v: V) => W2, V, W1, W2, Y>(
    f: F,
    g: G,
    h: H
  ): ConvergeValue<V, W1, W2, Y>;
}

/**
 * @see Converge
 */
export default <F extends (w1: W1, w2: W2) => Y, G extends (v: V) => W1, H extends (v: V) => W2, V, W1, W2, Y>(
  f: F,
  g: G,
  h: H,
  value: V
): Y => f(g(value), h(value));
